import { CloudArrowDownIcon, MagnifyingGlassIcon } from '@phosphor-icons/react';
import { useState } from 'react';

import { ICON_WEIGHT } from '@/components/icon';
import { Button } from '@/components/ui/button';
// Deep import: `features/sources` reaches back into this feature's `draft.ts`,
// so going through its barrel would close a module-level cycle.
import { usePreferredSource, useTrackSource } from '@/features/sources/use-sources';
import type { Flight, LogbookEntry } from '@/lib/db';
import { formatLocalDate, formatLocalTime } from '@/lib/format';
import { SourceError } from '@/lib/sources';

import {
  acceptApiMatch,
  applyProposals,
  defaultAcceptedIds,
  type FieldProposal,
  findApiMatches,
  type SuggestedMatch,
} from './api-match';
import { CONFIDENCE_LABEL } from './match';

/**
 * "Find this flight" — the logbook-side half of API matching.
 *
 * Sits in the Track column next to the existing link control, and for the same
 * reason that one sits outside `LOGBOOK_COLUMNS`: a track is a relationship
 * between records, not a value a pilot types, fills down or undoes.
 *
 * The flow it drives is three explicit steps (`api-match.ts`), and the panel
 * shows the cost and the reasoning at each one rather than presenting a
 * verdict. In particular the suggested match is never pre-accepted, and the
 * diff arrives with every conflicting field switched *off* — `DATA-SOURCES.md`
 * §4: keep the pilot's typed values over derived ones on conflict.
 */

type Stage =
  | { kind: 'closed' }
  | { kind: 'searching' }
  | { kind: 'candidates'; matches: SuggestedMatch[] }
  | { kind: 'fetching' }
  | { kind: 'proposals'; proposals: FieldProposal[]; trackId: string }
  | { kind: 'done'; message: string }
  | { kind: 'error'; message: string };

const CONFIDENCE_CLASS: Record<string, string> = {
  strong: 'text-foreground',
  possible: 'text-muted-foreground',
  weak: 'text-muted-foreground',
};

export interface ApiMatchCellProps {
  entry: LogbookEntry;
  flight: Flight | undefined;
}

export function ApiMatchCell({ entry, flight }: ApiMatchCellProps) {
  const preferred = usePreferredSource();
  const source = useTrackSource(preferred ?? undefined);
  const [stage, setStage] = useState<Stage>({ kind: 'closed' });
  const [accepted, setAccepted] = useState<ReadonlySet<string>>(new Set());

  if (
    preferred === null ||
    preferred === undefined ||
    source === undefined ||
    flight === undefined
  ) {
    return null;
  }

  /*
   * An entry that already has a track has nothing to search for — but the
   * panel has to outlive the moment it *gets* one. Accepting a match links
   * the track, which is exactly what flips this condition, and a component
   * that vanished on success would take its own field-by-field diff with it
   * before the pilot could answer it. The browser test caught this; the unit
   * test could not, because it renders this component directly with a fixed
   * flight prop rather than through the cell that re-renders around it.
   */
  const hasTrack = flight.trackId !== undefined;
  if (hasTrack && stage.kind === 'closed') {
    return null;
  }

  const close = () => {
    setStage({ kind: 'closed' });
  };

  const search = async () => {
    setStage({ kind: 'searching' });
    try {
      const matches = await findApiMatches(entry, flight, source);
      setStage({ kind: 'candidates', matches });
    } catch (caught) {
      setStage({
        kind: 'error',
        message:
          caught instanceof SourceError
            ? caught.message
            : `${preferred.label} could not be searched for this flight.`,
      });
    }
  };

  const accept = async (match: SuggestedMatch) => {
    setStage({ kind: 'fetching' });
    const result = await acceptApiMatch(entry, source, match.candidate);
    if (result.outcome === 'failed') {
      setStage({ kind: 'error', message: result.message });
      return;
    }
    setAccepted(new Set(defaultAcceptedIds(result.proposals)));
    if (result.proposals.length === 0) {
      setStage({
        kind: 'done',
        message: 'Track linked. Agrees with everything already entered.',
      });
      return;
    }
    setStage({ kind: 'proposals', proposals: result.proposals, trackId: result.track.id });
  };

  const apply = async (proposals: readonly FieldProposal[]) => {
    await applyProposals(entry, proposals, [...accepted]);
    setStage({
      kind: 'done',
      message:
        accepted.size === 0
          ? 'Track linked. Typed values were left unchanged.'
          : `Track linked. ${accepted.size} ${accepted.size === 1 ? 'field' : 'fields'} taken from it.`,
    });
  };

  const estimate = source.estimateCost?.('search', 3) ?? null;

  return (
    <span className="relative">
      {hasTrack ? null : (
        <button
          type="button"
          className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-xs"
          title={`Search ${preferred.label} for the flight this entry describes`}
          onClick={() => {
            void search();
          }}
        >
          <MagnifyingGlassIcon aria-hidden="true" weight={ICON_WEIGHT} className="size-3.5" />
          Find
        </button>
      )}

      {stage.kind === 'closed' ? null : (
        /*
          A centred overlay rather than a dropdown anchored to this cell, and
          the browser is what settled it: the grid scrolls horizontally
          (`overflow-x-auto` on a 22-column table), so an absolutely
          positioned panel inside a cell is clipped by that scroll container —
          the Playwright run could see the panel and could not click the
          button on it. Nothing about `z-index` fixes clipping. Taking it out
          of the flow entirely also makes it usable at phone width, where a
          384px dropdown hanging off a cell has nowhere to go.
        */
        <div className="bg-background/60 fixed inset-0 z-(--z-overlay) flex items-start justify-center overflow-y-auto p-4 backdrop-blur-sm sm:items-center">
          <div
            className="border-border bg-surface rounded-container my-auto w-full max-w-md space-y-2 border p-3 text-left shadow-lg"
            data-testid="api-match-panel"
          >
            {stage.kind === 'searching' ? (
              <p className="text-muted-foreground text-xs">Searching {preferred.label}…</p>
            ) : null}

            {stage.kind === 'fetching' ? (
              <p className="text-muted-foreground text-xs">Downloading the track…</p>
            ) : null}

            {stage.kind === 'error' ? (
              <p role="alert" className="text-danger text-xs">
                {stage.message}
              </p>
            ) : null}

            {stage.kind === 'done' ? <p className="text-xs">{stage.message}</p> : null}

            {stage.kind === 'candidates' ? (
              <>
                <p className="text-muted-foreground text-xs">
                  {stage.matches.length === 0
                    ? `${preferred.label} has nothing for ${entry.registration ?? 'this aircraft'} around ${entry.date}.`
                    : `${preferred.label}, around ${entry.date}. Nothing has been downloaded yet.`}
                </p>
                {estimate === null || stage.matches.length === 0 ? null : (
                  <p className="text-muted-foreground text-xs">
                    Downloading one track costs{' '}
                    {(source.estimateCost?.('fetch', 1)?.credits ?? 0) > 0
                      ? `about ${source.estimateCost?.('fetch', 1)?.credits ?? 0} credit`
                      : 'nothing'}
                    .
                  </p>
                )}
                <ul className="space-y-1">
                  {stage.matches.map((match) => (
                    <li
                      key={match.candidate.ref}
                      className="border-border rounded-control border p-2"
                    >
                      <p className="text-xs">
                        {match.candidate.startTime === undefined
                          ? 'Time unknown'
                          : `${formatLocalDate(match.candidate.startTime)} · `}
                        {match.candidate.startTime === undefined ? null : (
                          <span className="tabular">
                            {formatLocalTime(match.candidate.startTime)}
                          </span>
                        )}
                        {match.candidate.registration === undefined
                          ? ''
                          : ` · ${match.candidate.registration}`}
                      </p>
                      <p
                        className={`text-xs ${CONFIDENCE_CLASS[match.score.confidence] ?? 'text-muted-foreground'}`}
                      >
                        {CONFIDENCE_LABEL[match.score.confidence]}
                      </p>
                      {/*
                      The reasoning, not just the verdict. A pilot deciding
                      whether to spend a credit needs to see that the date
                      matched and the registration was never checked, rather
                      than a percentage with nothing behind it.
                    */}
                      <ul className="text-muted-foreground mt-1 space-y-0.5 text-xs">
                        {match.score.signals.map((signal) => (
                          <li key={signal.id}>
                            {signal.label}: {signal.detail}
                            {signal.score === null ? ' (not compared)' : ''}
                          </li>
                        ))}
                      </ul>
                      <Button
                        size="sm"
                        variant="outline"
                        className="mt-1.5"
                        onClick={() => {
                          void accept(match);
                        }}
                      >
                        <CloudArrowDownIcon
                          aria-hidden="true"
                          weight={ICON_WEIGHT}
                          className="size-4"
                        />
                        This is the one
                      </Button>
                    </li>
                  ))}
                </ul>
              </>
            ) : null}

            {stage.kind === 'proposals' ? (
              <>
                <p className="text-xs">
                  Track linked. Fields that differ from typed values are listed below.
                </p>
                {entry.isDraft ? null : (
                  <p className="text-danger text-xs">
                    Entry is approved and locked. Move it back to draft to apply changes.
                  </p>
                )}
                <ul className="space-y-1">
                  {stage.proposals.map((proposal) => (
                    <li key={proposal.columnId}>
                      <label className="flex items-start gap-2 text-xs">
                        <input
                          type="checkbox"
                          className="accent-accent mt-0.5 size-3.5"
                          disabled={!entry.isDraft}
                          checked={accepted.has(proposal.columnId)}
                          onChange={() => {
                            setAccepted((current) => {
                              const next = new Set(current);
                              if (next.has(proposal.columnId)) {
                                next.delete(proposal.columnId);
                              } else {
                                next.add(proposal.columnId);
                              }
                              return next;
                            });
                          }}
                        />
                        <span>
                          <span className="text-foreground">{proposal.label}</span>
                          {': '}
                          <span className="text-muted-foreground">
                            track says <span className="text-foreground">{proposal.proposed}</span>
                            {proposal.conflict ? (
                              <>
                                , entry has{' '}
                                <span className="text-foreground">{proposal.typed}</span>
                              </>
                            ) : (
                              ', entry is blank'
                            )}
                          </span>
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    size="sm"
                    disabled={!entry.isDraft}
                    onClick={() => {
                      void apply(stage.proposals);
                    }}
                  >
                    {accepted.size === 0
                      ? 'Keep typed values'
                      : `Take ${accepted.size} from the track`}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={close}>
                    Later
                  </Button>
                </div>
              </>
            ) : null}

            {stage.kind === 'proposals' ? null : (
              <button
                type="button"
                className="text-muted-foreground hover:text-foreground text-xs"
                onClick={close}
              >
                Close
              </button>
            )}
          </div>
        </div>
      )}
    </span>
  );
}
